import { useQuery } from "@tanstack/react-query";
import { History, Settings } from "lucide-react";
import type { RouteObject } from "react-router";
import { usePermissionChecker } from "@/lib/permissions";
import account from "./account";
import activity from "./activity";
import ai from "./ai";
import automations from "./automations";
import calendar from "./calendar";
import catalog from "./catalog";
import clients from "./clients";
import contracts from "./contracts";
import dashboard from "./dashboard";
import devui from "./devui";
import expenses from "./expenses";
import forms from "./forms";
import fx from "./fx";
import inbox from "./inbox";
import integrations from "./integrations";
import invoices from "./invoices";
import leads from "./leads";
import payments from "./payments";
import platform from "./platform";
import { platformApi, platformKeys } from "./platform/api";
import portal from "./portal";
import projects from "./projects";
import proofs from "./proofs";
import proposals from "./proposals";
import reports from "./reports";
import settings from "./settings";
import support from "./support";
import tasks from "./tasks";
import templates from "./templates";
import time from "./time";
import workload from "./workload";
import {
  NAV_GROUPS,
  NAV_GROUP_ORDER,
  navItemKey,
  type CommandItem,
  type FeatureModule,
  type NavGroupKey,
  type NavItem,
  type QuickCreateItem,
  type SettingsSection,
} from "./types";

/**
 * THE registry. Every feature is imported statically here (so the module graph —
 * and therefore the nav, the palette and the settings pages — is known at build
 * time) while every PAGE inside a feature is loaded lazily through its routes.
 *
 * Adding a feature: create `src/features/<key>/index.tsx`, then add two lines
 * here (the import and the array entry). Nothing else in the shell changes.
 */
export const features: readonly FeatureModule[] = [
  dashboard,
  inbox,
  leads,
  proposals,
  forms,
  clients,
  contracts,
  projects,
  tasks,
  time,
  workload,
  calendar,
  proofs,
  templates,
  invoices,
  payments,
  expenses,
  fx,
  reports,
  automations,
  integrations,
  ai,
  portal,
  account,
  activity,
  settings,
  support,
  platform,
  catalog,
  devui,
];

export const featuresByKey: ReadonlyMap<string, FeatureModule> = new Map(features.map((f) => [f.key, f]));

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

/** Authenticated pages, mounted inside <AppShell/>. */
export const featureRoutes: RouteObject[] = features.flatMap((f) => f.routes ?? []);
/** Client-portal pages, mounted inside <PortalShell/> at /portal. */
export const portalFeatureRoutes: RouteObject[] = features.flatMap((f) => f.portalRoutes ?? []);
/** Unauthenticated pages (/p/:token, /i/:token, /f/:slug, /book/:slug). */
export const publicFeatureRoutes: RouteObject[] = features.flatMap((f) => f.publicRoutes ?? []);

/**
 * True when some feature already serves this first path segment. The router uses
 * it so the placeholders it keeps for not-yet-built areas (activity, settings)
 * disappear the moment their feature registers real routes.
 */
export function hasRouteFor(segment: string): boolean {
  const wanted = segment.replace(/^\//, "");
  return featureRoutes.some((r) => (r.path ?? "").replace(/^\//, "").split("/")[0] === wanted);
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

/**
 * Nav entries that do not belong to a product feature. Activity log and Settings
 * are built by the workspace team; their routes are registered by their own
 * feature modules — these entries only reserve their place in the sidebar.
 */
export const CORE_NAV: readonly NavItem[] = [
  {
    key: "activity",
    label: "Activity log",
    to: "/activity",
    icon: History,
    group: "workspace",
    order: 30,
    permission: "audit:view",
    description: "Who changed what, when, and from where",
  },
  {
    key: "settings",
    label: "Settings",
    to: "/settings",
    icon: Settings,
    group: "workspace",
    order: 40,
    description: "Workspace, team, billing and preferences",
  },
];

function byOrder(a: NavItem, b: NavItem): number {
  const ga = NAV_GROUP_ORDER[a.group] - NAV_GROUP_ORDER[b.group];
  if (ga !== 0) return ga;
  const oa = (a.order ?? 100) - (b.order ?? 100);
  return oa !== 0 ? oa : a.label.localeCompare(b.label);
}

export const navItems: readonly NavItem[] = [...features.flatMap((f) => f.nav ?? []), ...CORE_NAV].sort(byOrder);

export interface NavGroup {
  key: NavGroupKey;
  label: string;
  items: NavItem[];
}

/** Groups (in display order) containing only the items the caller may see. */
export function navGroupsFor(can: (check: NavItem["permission"], mode?: "all" | "any") => boolean): NavGroup[] {
  const groups: NavGroup[] = [];
  for (const g of NAV_GROUPS) {
    const items = navItems.filter((i) => i.group === g.key && can(i.permission, i.permissionMode ?? "all"));
    if (items.length) groups.push({ key: g.key, label: g.label, items });
  }
  return groups;
}

/** Sidebar data for the current session. */
export function useNavGroups(): NavGroup[] {
  const can = usePermissionChecker();
  return navGroupsFor(can);
}

/** Flat list of visible nav items (⌘K "Jump to", shortcuts help). */
export function useVisibleNavItems(): NavItem[] {
  const can = usePermissionChecker();
  return navItems.filter((i) => can(i.permission, i.permissionMode ?? "all"));
}

export { navItemKey };

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export const quickCreateItems: readonly QuickCreateItem[] = features
  .flatMap((f) => f.quickCreate ?? [])
  .sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.label.localeCompare(b.label));

export const featureCommands: readonly CommandItem[] = features
  .flatMap((f) => f.commands ?? [])
  .sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.label.localeCompare(b.label));

/** "+" menu entries the current session may use. */
export function useQuickCreateItems(): QuickCreateItem[] {
  const can = usePermissionChecker();
  return quickCreateItems.filter((i) => can(i.permission, i.permissionMode ?? "all"));
}

/**
 * Extra palette commands (the "Go to …" ones are derived from the nav).
 *
 * Commands for the Lean2Automate console wait for `platformApi.me` to answer
 * 200. The probe runs only while the palette is open (`probe`), so a customer
 * who never opens it never asks, and it shares the console page's query, so a
 * platform admin asks once. A 403 on first load raises no toast.
 */
export function useFeatureCommands(probe = true): CommandItem[] {
  const can = usePermissionChecker();
  const platformAccess = useQuery({
    queryKey: platformKeys.me(),
    queryFn: ({ signal }) => platformApi.me(signal),
    enabled: probe,
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  return featureCommands.filter(
    (i) => can(i.permission, i.permissionMode ?? "all") && (i.audience !== "platform" || platformAccess.isSuccess),
  );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const settingsSections: readonly SettingsSection[] = features
  .flatMap((f) => f.settingsSections ?? [])
  .sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.label.localeCompare(b.label));

/** Settings pages the current session may open, in sidebar order. */
export function useSettingsSections(): SettingsSection[] {
  const can = usePermissionChecker();
  return settingsSections.filter((s) => can(s.permission, s.permissionMode ?? "all"));
}
